/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { nlNL } from '@mui/material/locale';
import { Linkable } from '@/components/blocks/Linkable';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { ContentContext } from '@/data/context/content';
import { Pagination, PaginationItem, PaginationRenderItemParams } from '@mui/material';
import { ChangeEvent, FC, useCallback, useContext } from 'react';
import { plpPaginationSX } from '../styles/container';
import { useDeviceType } from '@/data/deviceDetection';
import { getPageNumberForPaginationItem } from '@/data/utils/getPageNumberForPaginationItem';

const EMPTY_OBJECT = {};

export const CatalogEntryListPagination: FC = () => {
	const {isMobile} = useDeviceType();
	const { pageCount, pageNumber, onPageChange, getPageLink } = useContext(ContentContext) as ReturnType<
		typeof useCatalogEntryList
	>;
	const onChange = useCallback(
		(_: ChangeEvent<unknown>, page: number) => onPageChange(page),
		[onPageChange]
	);

	const renderItem = useCallback(
		(item: PaginationRenderItemParams) => {
			const { page, type, disabled } = item;
			const hidePreviousButton = type === "previous" && pageNumber === 1;
        	const hideNextButton = type === "next" && pageNumber === pageCount; 
			if (type === "previous" && pageNumber === 1) {
            	return null; // Hide previous arrow on first page
			}
			if (type === "next" && pageNumber === pageCount) {
				return null; // Hide next arrow on last page
			}
			const href = 
			page !== null
				? { href: getPageLink(getPageNumberForPaginationItem({ page, type, disabled })) }
				: EMPTY_OBJECT;
			return <PaginationItem 
						component={Linkable} {...href} {...item} 
						legacyBehavior={false} 
						rel={`${	(item.type === "previous" && item.page !== 0  )? "prev" :  (item.type === "next" && !((item?.page??0)>(pageCount??0)) ) ? "next":"" }`} 
						aria-label={nlNL.components?.MuiPagination?.defaultProps?.getItemAriaLabel
						? nlNL.components.MuiPagination.defaultProps.getItemAriaLabel(item.type, item.page ?? 0, !!pageCount)
						: undefined
					}
					disabled={item.disabled || hidePreviousButton || hideNextButton}
						/>;
		},
		[getPageLink]
	);
	
	
	/* Commenting as this is not used currently
	const pageLimit = PRODUCT_LIST_DEFAULT_PAGE_SIZE;
	const path = usePathname();
	const {settings} = useSettings();
    const basePath = settings.userData.SiteLink_DomainName;
	const fullPath = basePath+path;
	const calculateOffset = (item:UsePaginationItem , currPage:number) =>{

	let prevOffset = item.type == "previous" ? fullPath+ ( ( currPage == 0 ||currPage == 1 ||currPage == 2) ?"":"?offset="+( ((currPage-2)*pageLimit))):""; 
	let nextOffset = item.type == "next" ? fullPath+( (currPage == 0 ) ?"":"?offset="+ ((item.page) && (item?.page-1)*pageLimit ) ):"";
	let normalOffset = (item.type == "page") ?fullPath+((currPage == 0  || ((item.page) && ((item?.page-1)*pageLimit ) ==0 ) ) ?"": (item?.page) && "?offset="+(item.page-1)*pageLimit):"";
	let returnLink = "";
	if(item.type == "next"){
		returnLink=nextOffset;
	}
	if(prevOffset && item.type == "previous" ){
		returnLink=prevOffset;
	}
	if(item.type == "page"){		
		returnLink=normalOffset;
	}
	if(item.type == "start-ellipsis" || item.type == "end-ellipsis" ){ 
		returnLink="";
	}
	return returnLink;
	}*/

	return pageCount && pageCount > 1 ? (
		<Pagination 
			count={pageCount} 
			shape="rounded" 
			page={pageNumber} 
			renderItem={renderItem} 
			onChange={onChange} 
			siblingCount={isMobile?0:1}
			sx={plpPaginationSX}
		/>
	) : null;
};
